import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { fetchCompanyById, updateCompany, createAccount, updateAccount, deleteAccount } from '../../store/companies';

function EditCompany() {
  const { id } = useParams();
  const parsedId = parseInt(id);
  const dispatch = useDispatch();
  const history = useHistory();
  const user = useSelector(state => state.session.user);
  const companies = useSelector(state => state.companies);

  // Look for the company in Redux store
  const company = companies.find(c => c.id === parsedId);

  const [companyName, setCompanyName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [errors, setErrors] = useState([]);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [editingAccount, setEditingAccount] = useState(null);

  // Form state for account modal
  const [accountName, setAccountName] = useState('');
  const [accountLocation, setAccountLocation] = useState('');
  const [yardCount, setYardCount] = useState(0);
  const [buildingCount, setBuildingCount] = useState(0);

  // Handler for numeric inputs to ensure integers
  const handleNumericInput = (setter, value) => {
    // Convert to integer or default to 0 if not a valid number
    const intValue = parseInt(value);
    setter(isNaN(intValue) ? 0 : intValue);
  };

  // Load company data using the Redux action
  useEffect(() => {
    const loadCompanyData = async () => {
      try {
        await dispatch(fetchCompanyById(parsedId));
        setIsLoading(false);
      } catch (error) {
        console.error('Error loading company data:', error);
        setErrors(['Failed to load company data: ' + error.toString()]);
        setIsLoading(false);
      }
    };

    loadCompanyData();
  }, [dispatch, parsedId]);

  // Set accounts from company data
  useEffect(() => {
    if (company && company.accounts) {
      setAccounts(company.accounts);
    }
  }, [company]);

  useEffect(() => {
    if (company) {
      setCompanyName(company.company_name || '');
    }
  }, [company]);

  // Update form fields when editing an account
  useEffect(() => {
    if (editingAccount) {
      setAccountName(editingAccount.account_name || '');
      setAccountLocation(editingAccount.location || '');
      setYardCount(editingAccount.yard_count || 0);
      setBuildingCount(editingAccount.building_count || 0);
    } else {
      // Reset form fields when adding a new account
      setAccountName('');
      setAccountLocation('');
      setYardCount(0);
      setBuildingCount(0);
    }
  }, [editingAccount]);

  // Handlers as callbacks to prevent unnecessary re-renders
  const handleAddAccount = useCallback(() => {
    setEditingAccount(null);
    setShowAccountModal(true);
  }, []);

  const handleEditAccount = useCallback((account) => {
    setEditingAccount(account);
    setShowAccountModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowAccountModal(false);
    setErrors([]);
  }, []);

  const handleSaveAccount = useCallback(async () => {
    // Validate required fields
    const errors = [];
    if (!accountName) errors.push('Account name is required');
    if (!accountLocation) {
      errors.push('Location is required');
    } else if (!/^[A-Za-z\s]+,\s*[A-Z]{2}$/.test(accountLocation)) {
      errors.push('Location must be in format "City, ST" (e.g., "Boston, MA")');
    }
    if (yardCount < 0) errors.push('Yard count must be 0 or greater');
    if (buildingCount < 0) errors.push('Building count must be 0 or greater');

    if (errors.length > 0) {
      setErrors(errors);
      return;
    }

    setErrors([]);

    const accountData = {
      account_name: accountName,
      location: accountLocation,
      yard_count: yardCount,
      building_count: buildingCount,
      company_id: parsedId
    };

    try {
      let response;
      if (editingAccount) {
        // Update existing account
        response = await dispatch(updateAccount(editingAccount.id, accountData));
      } else {
        // Create new account
        response = await dispatch(createAccount(accountData));
      }

      // Close modal only on success
      setShowAccountModal(false);

      // Refresh company data to get updated accounts
      await dispatch(fetchCompanyById(parsedId));

      return response;
    } catch (error) {
      console.error('Error saving account:', error);
      // Parse the error message if it's in JSON format
      let errorMessage = error.toString();
      try {
        if (errorMessage.includes('{') && errorMessage.includes('}')) {
          // Try to extract JSON part of the error
          const jsonStr = errorMessage.substring(
            errorMessage.indexOf('{'),
            errorMessage.lastIndexOf('}') + 1
          );
          const errorObj = JSON.parse(jsonStr);
          if (errorObj.error) {
            errorMessage = errorObj.error;
          }
        }
      } catch (parseError) {
        // If parsing fails, use the original error message
        console.warn('Failed to parse error message:', parseError);
      }

      setErrors([`Failed to save account: ${errorMessage}`]);
      return null;
    }
  }, [dispatch, accountName, accountLocation, yardCount, buildingCount, parsedId, editingAccount]);

  const handleDeleteAccount = useCallback(async (accountId) => {
    if (!window.confirm('Are you sure you want to delete this account?')) {
      return;
    }

    try {
      await dispatch(deleteAccount(accountId, parsedId));
      // Refresh company data to get updated accounts
      await dispatch(fetchCompanyById(parsedId));
    } catch (error) {
      console.error('Error deleting account:', error);
      setErrors([error.toString()]);
    }
  }, [dispatch, parsedId]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setErrors([]);

    if (!companyName.trim()) {
      setErrors(['Company name is required']);
      return;
    }

    try {
      setIsSubmitting(true);
      await dispatch(updateCompany(id, { company_name: companyName.trim() }));
      history.push('/companies');
    } catch (error) {
      console.error('Error updating company:', error);
      setErrors(error.toString().split(','));
    } finally {
      setIsSubmitting(false);
    }
  }, [companyName, dispatch, history, id]);

  // Redirect non-admin users after all hooks are declared
  if (!user || !user.is_admin) {
    history.push('/');
    return null;
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
          <div className="ml-4 text-2xl text-blue-600">Loading...</div>
        </div>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="p-5 flex flex-col items-center">
        <div className="text-7xl mb-6">Company not found</div>

        <div className="bg-yellow-100 p-6 rounded-lg max-w-4xl">
          <h2 className="text-3xl font-semibold mb-4">Debug Information</h2>
          <p className="text-xl mb-2">Requested Company ID: {parsedId}</p>
          <p className="text-xl mb-2">Companies in Redux: {companies.length}</p>

          <button
            onClick={() => history.push('/companies')}
            className="mt-6 px-6 py-3 text-2xl text-white bg-blue-500 rounded-lg hover:bg-blue-600"
          >
            Back to Companies
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-5 min-h-[calc(100vh-100px)] flex flex-col">
      <div className="max-w-6xl mx-auto w-full">
        <div className="bg-white rounded-lg shadow-md p-8 mb-8">
          <h2 className="text-6xl font-bold text-blue-600 mb-6">Edit Company</h2>

          {errors.length > 0 && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6" role="alert">
              <p className="font-bold text-2xl">Please fix the following errors:</p>
              <ul className="list-disc pl-10 mt-2">
                {errors.map((error, idx) => (
                  <li key={idx} className="text-xl">{error}</li>
                ))}
              </ul>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="companyName" className="block text-2xl font-medium text-gray-700 mb-2">
                Company Name
              </label>
              <input
                id="companyName"
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                className="w-full px-4 py-3 text-2xl border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter company name"
                required
              />
            </div>

            <div className="flex gap-4 pt-4">
              <button
                type="button"
                onClick={() => history.push('/companies')}
                className="px-6 py-3 text-2xl text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className={`px-6 py-3 text-2xl text-white bg-blue-500 rounded-lg hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isSubmitting ? 'opacity-75 cursor-not-allowed' : ''
                }`}
              >
                {isSubmitting ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>

        {/* Accounts Section */}
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-4xl font-bold text-blue-600">Accounts</h3>
            <button
              onClick={handleAddAccount}
              className="px-6 py-3 text-xl text-white bg-green-500 rounded-lg hover:bg-green-600 transition-colors"
            >
              Add Account
            </button>
          </div>

          {accounts.length === 0 ? (
            <div className="text-2xl text-gray-500 text-center p-8 bg-gray-100 rounded-lg">
              No accounts found for this company.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xl font-medium text-gray-500 uppercase tracking-wider">Account Name</th>
                    <th className="px-6 py-3 text-left text-xl font-medium text-gray-500 uppercase tracking-wider">Location</th>
                    <th className="px-6 py-3 text-left text-xl font-medium text-gray-500 uppercase tracking-wider">Yards</th>
                    <th className="px-6 py-3 text-left text-xl font-medium text-gray-500 uppercase tracking-wider">Buildings</th>
                    <th className="px-6 py-3 text-right text-xl font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {accounts.map((account) => (
                    <tr key={account.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-xl font-medium text-gray-900">{account.account_name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-xl text-gray-700">{account.location}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-xl text-gray-700">{account.yard_count}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-xl text-gray-700">{account.building_count}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-xl font-medium">
                        <button
                          onClick={() => handleEditAccount(account)}
                          className="text-blue-600 hover:text-blue-900 mr-4"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteAccount(account.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Account Modal */}
      {showAccountModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div
            className="bg-white rounded-lg p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-4xl font-bold mb-6 text-blue-600">
              {editingAccount ? 'Edit Account' : 'Add New Account'}
            </h3>

            {errors.length > 0 && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6" role="alert">
                <p className="font-bold text-2xl">Please fix the following errors:</p>
                <ul className="list-disc pl-10 mt-2">
                  {errors.map((error, idx) => (
                    <li key={idx} className="text-xl">{error}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="space-y-6">
              <div>
                <label htmlFor="account-name" className="block text-2xl font-medium text-gray-700 mb-2">
                  Account Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="account-name"
                  type="text"
                  value={accountName}
                  onChange={(e) => setAccountName(e.target.value)}
                  className="w-full px-4 py-3 text-2xl border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter account name"
                  required
                  autoFocus
                />
              </div>

              <div>
                <label htmlFor="account-location" className="block text-2xl font-medium text-gray-700 mb-2">
                  Location <span className="text-red-500">*</span>
                </label>
                <input
                  id="account-location"
                  type="text"
                  value={accountLocation}
                  onChange={(e) => setAccountLocation(e.target.value)}
                  className="w-full px-4 py-3 text-2xl border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g. Chicago, IL"
                  pattern="^[A-Za-z\s]+,\s*[A-Z]{2}$"
                  title="City, State format (e.g. Boston, MA)"
                  required
                />
                <p className="text-gray-500 text-lg mt-1">Enter city name and state abbreviation (e.g. Boston, MA)</p>
              </div>

              <div>
                <label htmlFor="yard-count" className="block text-2xl font-medium text-gray-700 mb-2">
                  Yard Count <span className="text-red-500">*</span>
                </label>
                <input
                  id="yard-count"
                  type="number"
                  min="0"
                  value={yardCount}
                  onChange={(e) => handleNumericInput(setYardCount, e.target.value)}
                  className="w-full px-4 py-3 text-2xl border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                  required
                />
                <p className="text-gray-500 text-lg mt-1">Number of yards for this account</p>
              </div>

              <div>
                <label htmlFor="building-count" className="block text-2xl font-medium text-gray-700 mb-2">
                  Building Count <span className="text-red-500">*</span>
                </label>
                <input
                  id="building-count"
                  type="number"
                  min="0"
                  value={buildingCount}
                  onChange={(e) => handleNumericInput(setBuildingCount, e.target.value)}
                  className="w-full px-4 py-3 text-2xl border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                  required
                />
                <p className="text-gray-500 text-lg mt-1">Number of buildings for this account</p>
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-6 py-3 text-2xl text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveAccount}
                  className="px-6 py-3 text-2xl text-white bg-blue-500 rounded-lg hover:bg-blue-600"
                >
                  {editingAccount ? 'Update Account' : 'Add Account'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EditCompany;
